import * as React from 'react'
import styled from 'styled-components'
import type { ReactNode, RefCallback } from 'react'
import { connect } from 'react-redux'
import { rem } from 'polished'
import { useLocation } from 'react-router-dom'
import { useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'

import BodyText from 'LuxKit/Typography/BodyText'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import config from 'constants/config'
import DestinationsCarousels from 'components/Common/DestinationsCarousel/DestinationCarousels'
import Group from 'components/utils/Group'
import IconButton from 'LuxKit/Button/IconButton'
import Image from 'components/Common/Image'
import LayoutContainer from 'components/Common/LayoutContainer'
import LineFacebookIcon from 'LuxKit/Icons/line/LineFacebookIcon'
import LineInstagramIcon from 'LuxKit/Icons/line/LineInstagramIcon'
import LineRssIcon from 'LuxKit/Icons/line/LineRssIcon'
import PinterestIcon from 'LuxKit/Icons/custom/PinterestIcon'
import TextLink from 'LuxKit/TextLink'
import TopDestinationList from 'components/Common/TopDestinationsList/TopDestinationList'
import type SvgIcon from 'LuxKit/SvgIcon'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { staticPageMessages } from 'constants/config/i18nMessages'
import { useAppFooterObserver } from 'hooks/useAppLayoutObservers'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'

const Container = styled.div`
  padding: ${rem(16)} 0;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  @media print {
    /* Hide footer entirely in print */
    display: none;

    a[href]:after {
      content: "";
    }
  }
`

const AppLogo = styled(Image)`
  max-width: 100%;
`

const AppLogoLink = styled(ClickableLink)`
  width: ${rem(128)};
`

const TopSectionWrapper = styled.div`
  padding: ${rem(32)} 0;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  text-align: center;

  ${mediaQueryUp.tablet} {
    text-align: start;
  }
`

interface SocialLink {
  href: string
  Icon: typeof SvgIcon
}

const SUPPORT_PAGE_ROUTE = '/:regionCode/support'

function CompactFooter() {
  const intl = useIntl()
  const currentYear = new Date().getFullYear()
  const containerRef = useRef<HTMLDivElement>(null)
  useAppFooterObserver(containerRef)
  const [footerRef, footerInView] = useInView({
    triggerOnce: true, // Load it once and keep it loaded
    rootMargin: '500px', // Trigger 500px before the component is reached
  })
  const combinedRefCallback = useCallback<RefCallback<HTMLDivElement>>((instance) => {
    footerRef(instance)
    containerRef.current = instance
  }, [footerRef])

  const whiteLabelAppConfig = useWhiteLabelAppConfig()
  const location = useLocation()
  const isSupportPage = !!matchLocaleAwarePath(location.pathname, { path: SUPPORT_PAGE_ROUTE, exact: true })
  const isHomePage = !!matchLocaleAwarePath(location.pathname, { path: '/:regionCode', exact: true })
  const isHotelsPage = !!matchLocaleAwarePath(location.pathname, { path: '/:regionCode/hotels', exact: true })
  const isExperiencesPage = !!matchLocaleAwarePath(location.pathname, { path: '/:regionCode/experiences', exact: true })
  const isToursPage = !!matchLocaleAwarePath(location.pathname, { path: '/:regionCode/tours', exact: true })
  const isFlightsPage = !!matchLocaleAwarePath(location.pathname, { path: '/:regionCode/flights', exact: true })
  const showVerticalDestinationList = isHotelsPage || isExperiencesPage || isToursPage || isFlightsPage

  const socialLinks = (() => {
    const links = new Map<string, SocialLink>()
    if (config.footer.links?.facebook) links.set('facebook', { href: config.footer.links.facebook, Icon: LineFacebookIcon })
    if (config.footer.links?.instagram) links.set('instagram', { href: config.footer.links.instagram, Icon: LineInstagramIcon })
    // we really did misspell pinterest huh...
    if (config.footer.links?.pintrest) links.set('pinterest', { href: config.footer.links.pintrest, Icon: PinterestIcon })
    if (config.footer.links?.blog) links.set('blog', { href: config.footer.links.blog, Icon: LineRssIcon })
    return Array.from(links.entries())
  })()

  const followUsTitle = intl.formatMessage({
    id: 'lC8-BswwIlRY0Ix70bhd',
    defaultMessage: 'Follow Us',
    description: 'Title for social media links section in compact footer',
  })

  const downloadAppTitle = intl.formatMessage({
    id: '8iWou6kkYXccHG_UdG7e',
    defaultMessage: 'Download our free app',
    description: 'Title for mobile app download section in compact footer',
  })

  const appStoreBadgeAltText = intl.formatMessage({
    id: 'srS8fBoHb63-nyua9ick',
    defaultMessage: 'App Store',
    description: 'Alternative text for the Apple App Store download badge image in the compact footer',
  })

  const googlePlayBadgeAltText = intl.formatMessage({
    id: 'yCkJWQlGbLcJmPtj6XCS',
    defaultMessage: 'Google Play',
    description: 'Alternative text for the Google Play download badge image in the compact footer',
  })

  const homeLink = intl.formatMessage({
    id: 'SyTzhdOaIF9zxR25pKiQ',
    defaultMessage: 'Home',
    description: 'Home link text in the compact footer in the site footer',
  })

  const faqsLink = intl.formatMessage({
    id: 'iiToMUCBPIzreJVT2kJx',
    defaultMessage: 'FAQs',
    description: 'FAQs link text in the compact footer in the site footer',
  })

  const copyrightText = intl.formatMessage(
    {
      id: 'hUdwoLHCGCGL4GMKWzKW',
      defaultMessage: '© {year} {title} - All Rights Reserved.',
      description: 'Copyright notice in compact footer with year and company title',
    },
    {
      year: currentYear,
      title: config.title,
    },
  )

  const termsAndConditionsLink = intl.formatMessage({
    id: 'rdxzJ66ZbjhVf72Mt2Zo',
    defaultMessage: 'Terms & Conditions',
    description: 'Terms and Conditions link text in the compact footer',
  })

  const privacyPolicyText = intl.formatMessage(staticPageMessages.privacyPolicy)

  const privacyPolicyInfoText = intl.formatMessage(
    {
      id: 'LlBawLV7Y24cXxOGoRBA',
      defaultMessage: 'To see how we manage your personal information and other details, please see our <link>{privacyPolicy}</link>.',
      description: 'Privacy policy information text with link in compact footer',
    },
    {
      privacyPolicy: privacyPolicyText,
      link: (chunks: ReactNode) => (
        <TextLink key="privacy-link" href="/privacy-policy">
          {chunks}
        </TextLink>
      ),
    },
  )

  return <Container ref={combinedRefCallback}>
    {footerInView && <TopSectionWrapper>
      {isHomePage && <DestinationsCarousels />}
      {isHomePage && <TopDestinationList />}
      {showVerticalDestinationList && (
        <TopDestinationList destinationNavItems={['Cities', 'Popular', 'Countries']} />
      )}
    </TopSectionWrapper>}

    <LayoutContainer size="lg">
      <Group direction="vertical" gap={32}>
        {!whiteLabelAppConfig.isApp && !isSupportPage && <Group
          direction="vertical"
          desktopDirection="horizontal"
          horizontalAlign="center"
          desktopHorizontalAlign="space-between"
          verticalAlign="center"
          gap={32}
        >
          <Group direction="horizontal" gap={16}>
            <TextLink href="/au" size="large">{homeLink}</TextLink>
            {config.header.faq && <TextLink href={config.header.faq.href} size="large">{faqsLink}</TextLink>}
          </Group>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="center" gap={24} wrap="wrap">
            {!!socialLinks.length && <Group direction="vertical" horizontalAlign="center" gap={8}>
              <BodyText variant="large" align="center" format="uppercase" weight="bold">{followUsTitle}</BodyText>
              <Group direction="horizontal" horizontalAlign="center" gap={8}>
                {socialLinks.map(([key, socialLink]) => <IconButton
                  key={key}
                  href={socialLink.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  kind="primary"
                  shape="circle"
                >
                  <socialLink.Icon />
                </IconButton>)}
              </Group>
            </Group>}
            {!whiteLabelAppConfig.isApp && <Group direction="vertical" horizontalAlign="center" gap={8}>
              <BodyText variant="large" align="center" format="uppercase" weight="bold">{downloadAppTitle}</BodyText>
              <Group direction="horizontal" horizontalAlign="center" gap={8}>
                {!!config.footer.links?.ios && <AppLogoLink href={config.footer.links.ios} target="_blank" rel="noopener noreferrer"><AppLogo id="apple_jch2pt" alt={appStoreBadgeAltText} /></AppLogoLink>}
                {!!config.footer.links?.android && <AppLogoLink href={config.footer.links.android} target="_blank" rel="noopener noreferrer"><AppLogo id="google_play_ytxm3z" alt={googlePlayBadgeAltText} /></AppLogoLink>}
              </Group>
            </Group>}
          </Group>
        </Group>}

        <Group direction="horizontal" horizontalAlign="center" desktopHorizontalAlign="start" gap={8} wrap="wrap">
          <BodyText variant="large">{copyrightText}</BodyText>
          <BodyText variant="large"><TextLink underline={false} href="/terms-and-conditions">{termsAndConditionsLink} </TextLink></BodyText>
          <BodyText variant="large"><TextLink underline={false} href="/privacy-policy">{privacyPolicyText}</TextLink></BodyText>
        </Group>

        <BodyText variant="large" align="center" desktopAlign="start">{privacyPolicyInfoText}</BodyText>

        <Group direction="horizontal" horizontalAlign="center" desktopHorizontalAlign="start" wrap="wrap" noDisplayWhenEmpty>
          {config.footer.partners.map(partner => <TextLink
            key={partner.name}
            target="_blank"
            rel="noopener noreferrer"
            href={partner.href}
            size="large"
          >
            {intl.formatMessage(
              {
                id: 'aZY_ts0ki40QKvN1ghWa',
                defaultMessage: 'Powered by {partnerName}',
                description: 'Powered by partner text in the compact footer in the site footer',
              },
              {
                partnerName: partner.name,
              },
            )}
          </TextLink>)}
        </Group>
      </Group>
    </LayoutContainer>
  </Container>
}

function mapStateToProps(state: App.State) {
  return {
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(CompactFooter)
